/**
 * The site navigation: one list, drawn three ways (docs/design/pages/navigation.md).
 *  - Desktop bar (Navbar.tsx): the five section labels. Each label links to its hub; a section with links
 *    under it gets a chevron button that opens a small panel listing the hub ("All ...") and those links.
 *  - Phone menu (MobileMenu.tsx): the five sections as rows, each section's links indented under it, then
 *    the community links.
 *  - Active state (activeNav below): one destination per page, the most specific match.
 *
 * Every destination the navbar had before the October 2026 rebuild is still here. navLinks.test.ts lists them,
 * so a later edit cannot drop one by accident.
 */
import { BookOpen, Calculator, FlaskConical, Layers, Scale, type LucideIcon } from "lucide-react";

export interface NavLinkItem {
  label: string;
  href: string;
  /**
   * Other paths this link stands for, matched like its href (the path itself or anything under it). They decide
   * which item lights up; they are never shown. Compound pages live at /compound/<slug>, for example, so they
   * light up Compounds.
   */
  match?: string[];
}

export interface NavSection extends NavLinkItem {
  id: string;
  /** The hub's label inside the desktop panel ("All compounds"), where the section label sits right above it. */
  hubLabel?: string;
  /** Phone menu row icon (lucide, with the label always next to it). */
  icon: LucideIcon;
  /** Links under the section: in its desktop panel after the hub, and indented under it in the phone menu. */
  links?: NavLinkItem[];
}

export interface CommunityLink {
  label: string;
  href: string;
}

export const NAV_SECTIONS: NavSection[] = [
  {
    id: "compounds",
    label: "Compounds",
    href: "/wiki",
    hubLabel: "All compounds",
    icon: FlaskConical,
    // Compound pages and the pages built per compound, plus the A to Z list (its canonical is /wiki).
    match: ["/compound", "/compounds", "/protocol", "/side-effects", "/alternatives-to"],
    links: [{ label: "Nootropics", href: "/nootropics", match: ["/nootropic"] }],
  },
  {
    id: "prices",
    label: "Compare prices",
    href: "/compare",
    hubLabel: "All prices",
    icon: Scale,
    match: ["/peptide-prices", "/buy"],
    links: [
      { label: "Vendors", href: "/vendors", match: ["/vendor", "/best-peptide-vendors", "/best-vendors", "/lab-results", "/labs"] },
      { label: "Trust scorecard", href: "/vendors/scorecard" },
      { label: "Deals", href: "/deals", match: ["/coupons"] },
    ],
  },
  {
    id: "tools",
    label: "Tools",
    href: "/tools",
    icon: Calculator,
  },
  {
    id: "stacks",
    // The public research hub. The stack builder (/stack-builder) needs an account, so it is not shown here;
    // signed-in visitors reach it from the account menu ("My stacks"). It still lights up Stacks.
    label: "Stacks",
    href: "/stack",
    icon: Layers,
    match: ["/stack-builder"],
  },
  {
    id: "guides",
    label: "Guides",
    href: "/guides",
    hubLabel: "All guides",
    icon: BookOpen,
    match: ["/beginner-guide", "/how-to-read-a-peptide-coa"],
    links: [
      { label: "Peptide cheat sheet", href: "/guides/peptide-cheat-sheet-download" },
      { label: "Blog", href: "/blog" },
      { label: "News", href: "/news" },
      { label: "Coaching", href: "/coaching", match: ["/coaches", "/coach"] },
    ],
  },
];

/** Shown with the Guides panel on desktop and as their own group in the phone menu. They open in a new tab. */
export const COMMUNITY_LINKS: CommunityLink[] = [
  { label: "Discord", href: "https://discord.gg/Mhq5UdRYBA" },
  { label: "r/BodyHackGuide", href: "https://reddit.com/r/BodyHackGuide" },
  { label: "r/BrainHackGuide", href: "https://reddit.com/r/BrainHackGuide" },
  { label: "r/Biohackingher", href: "https://reddit.com/r/Biohackingher" },
];

/** Every internal destination with the section it belongs to, hubs first. */
export const NAV_DESTINATIONS: { item: NavLinkItem; sectionId: string }[] = NAV_SECTIONS.flatMap((section) => [
  { item: section, sectionId: section.id },
  ...(section.links ?? []).map((item) => ({ item, sectionId: section.id })),
]);

function matchesPath(pathname: string, path: string): boolean {
  return pathname === path || pathname.startsWith(path + "/");
}

export interface ActiveNav {
  /** The href of the one destination the page belongs to. */
  href: string;
  /** The section that destination sits in, which the desktop bar lights up. */
  sectionId: string;
}

/**
 * The one destination the current page belongs to: of every href and match path that is the page itself or a
 * parent of it, the longest wins. /vendors/scorecard is Trust scorecard (not Vendors), /compound/bpc-157 is
 * Compounds, the homepage is nothing. A page lights one item, never two.
 */
export function activeNav(pathname: string): ActiveNav | null {
  const path = pathname.length > 1 ? pathname.replace(/\/+$/, "") : pathname;
  let best: { length: number; href: string; sectionId: string } | null = null;
  for (const { item, sectionId } of NAV_DESTINATIONS) {
    for (const candidate of [item.href, ...(item.match ?? [])]) {
      if (matchesPath(path, candidate) && (!best || candidate.length > best.length)) {
        best = { length: candidate.length, href: item.href, sectionId };
      }
    }
  }
  return best ? { href: best.href, sectionId: best.sectionId } : null;
}

/** "Ctrl K" or "Cmd K", for the search shortcut hint on desktop. */
export function searchShortcutLabel(): string {
  if (typeof navigator === "undefined") return "Ctrl K";
  const platform =
    (navigator as Navigator & { userAgentData?: { platform?: string } }).userAgentData?.platform || navigator.platform || "";
  return /mac|iphone|ipad/i.test(platform) ? "Cmd K" : "Ctrl K";
}
